import React, { useEffect } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import * as DT from '@/engine/PlanningAlgorithm/DataTypes';
import { reaction } from 'mobx';
import type { IReactionDisposer } from 'mobx';
import { TYPES } from '@/engine/ioc';
import { useInjection } from 'inversify-react';
import type { ViewsService } from '@/engine/app/Views.service';
import type { IObjectViewModel } from '@/engine/app/interfaces';
import { useTheme } from '@mui/material/styles';
import { getConditionalStyles } from '../styles';
import { UpdatableValue } from '../common/UpdatableValue.tsx';
import type { MeasurementBoxesController } from '.';

export interface VolumeMeasurementProps {
  label: string;
  descriptor: string;
  attachments: Array<string>;
}

interface VolumeMeasurementBoxProps {
  measurements: Array<VolumeMeasurementProps>;
  onChangeHighlighting: (descriptor: string, highlighted: boolean) => void;
}

export const VolumeMeasurementBox: React.FC<VolumeMeasurementBoxProps> = (props) => {
  const theme = useTheme();

  const mainController = useInjection<MeasurementBoxesController>(TYPES.IMeasurementsBoxesController);
  const viewsService = useInjection<ViewsService>(TYPES.ViewsService);
  const [selected, setSelected] = React.useState<Array<string>>([]);

  const isSelected = (descriptor: string): boolean => selected.includes(descriptor);

  const objectVMs: Array<IObjectViewModel> | undefined = viewsService.currentView?.getObjects();

  useEffect(() => {
    const highlightingReactionDisposers: Array<IReactionDisposer> = [];
    if (objectVMs) {
      objectVMs.forEach((viewModel) => {
        highlightingReactionDisposers.push(
          reaction(
            () => viewModel.isHighlighted,
            (highlighting: boolean) => {
              if (highlighting) {
                setSelected((prevSelected) => prevSelected.concat(viewModel.attachments));
              } else {
                setSelected((prevSelected) =>
                  prevSelected.filter((element) => !viewModel.attachments.includes(element))
                );
              }
            }
          )
        );
      });
    }
    return (): void => {
      highlightingReactionDisposers.forEach((disposer) => disposer());
    };
  }, [objectVMs]);

  return (
    <TableContainer component={Paper}>
      <Table aria-label="a dense table" size="small">
        <TableBody>
          {props.measurements.map((measurement: VolumeMeasurementProps, index) => {
            const isItemSelected = isSelected(measurement.descriptor);
            return (
              <TableRow
                key={measurement.label}
                data-testid={measurement.descriptor + 'Volume'}
                sx={{
                  '&:last-child td, &:last-child th': { border: 0 },
                  ...getConditionalStyles(isItemSelected, theme)
                }}
                onMouseEnter={(): void =>
                  measurement.attachments.forEach((attachment: string) =>
                    mainController.setObjectHighlighting(attachment, true)
                  )
                }
                onMouseLeave={(): void =>
                  measurement.attachments.forEach((attachment: string) =>
                    mainController.setObjectHighlighting(attachment, false)
                  )
                }
              >
                <TableCell component="th" scope="row" key={'label' + index}>
                  {measurement.label}
                </TableCell>

                <TableCell align="right" key={'value' + index} sx={{ '&': { paddingRight: 1.5 } }}>
                  <UpdatableValue
                    descriptor={measurement.descriptor}
                    constructor={DT.NumberT}
                    valueKey="value"
                    formatting={(n): string => (n / 1000).toFixed(1)}
                    isUndefinedOk={true}
                  />{' '}
                  cm³
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
